class AlertAudioService {
  constructor() {
    this.audio = null;
    this.audioCtx = null;
    this.isUnlocked = false;
    this.isMuted = true;
    this.lastPlayedTsMap = new Map();
  }

  init() {
    if (this.audio) return;

    this.audio = new Audio(
      "https://daksh-dev.s3.ap-south-1.amazonaws.com/frontend-static-files/sound_alert.mp3"
    );
    this.audio.preload = "auto";
    this.audio.volume = 1.0;

    this.audio.onerror = (e) => {
      console.error("❌ Audio load failed (S3):", e);
    };

    // Initialize simplified AudioContext just to track state
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        this.audioCtx = new AudioContextClass();
      }
    } catch (e) {
      console.warn("AudioContext init failed:", e);
    }
  }

  // Called on ANY user interaction (click, touch) to unlock audio
  unlock() {
    this.init();

    // 1. Resume Audio Context if suspended
    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      this.audioCtx.resume().then(() => {
        // console.log("🔊 AudioContext resumed");
      }).catch(e => console.warn("Context resume failed", e));
    }

    // 2. To strictly "unlock" the Audio element on iOS/Chrome, we must play() it.
    // We play and immediately pause so the user doesn't hear a blip, 
    // but the browser whitelists the element.
    if (this.audio) {
      // If already unlocked and playing, don't interrupt
      if (this.isUnlocked && !this.audio.paused) return;

      const p = this.audio.play();
      if (p !== undefined) {
        p.then(() => {
          this.audio.pause();
          this.audio.currentTime = 0;
          this.isUnlocked = true;
          window.dispatchEvent(new CustomEvent("audioUnlocked"));
        }).catch((e) => {
          // AbortError is benign (e.g., rapid navigation), others mean we are locked
          if (e.name !== 'AbortError') {
            console.warn("Audio unlock failed:", e);
            this.isUnlocked = false;
            window.dispatchEvent(new CustomEvent("audioLocked"));
          }
        });
      }
    }
  }

  setMuted(value) {
    this.isMuted = value;
    if (value && this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
    }
    // If unmuting, try to unlock properly
    if (!value) {
      this.unlock();
    }
  }

  playAlert(timestamp, patientID = "global") {
    if (this.isMuted) return;

    // Last-ditch init if missing
    if (!this.audio) this.init();

    // Prevent duplicate plays for the SAME timestamp for the SAME patient
    const lastPatientTs = this.lastPlayedTsMap.get(patientID) || 0;
    if (timestamp && timestamp <= lastPatientTs) return;

    // If locked, we try to unlock, but it might fail without user gesture.
    // If it fails, the "audioLocked" event will trigger the banner.
    if (!this.isUnlocked) {
      // Try to resume context at least
      if (this.audioCtx && this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }
    }

    // Attempt Play
    const p = this.audio.play();
    if (p !== undefined) {
      p.then(() => {
        // Success!
        this.isUnlocked = true;
        this.lastPlayedTsMap.set(patientID, timestamp);
        // Dispatch unlocked just in case
        window.dispatchEvent(new CustomEvent("audioUnlocked"));
      }).catch((e) => {
        console.warn(`❌ Play failed for ${patientID}:`, e);
        // If not allowed, mark as locked so banner shows up
        if (e.name === "NotAllowedError" || e.name === "NotSupportedError") {
          this.isUnlocked = false;
          window.dispatchEvent(new CustomEvent("audioLocked"));
        }
      });
    }
  }
}

export const AlertAudio = new AlertAudioService();
